// YardDetailsModal.jsx
import React from "react";
import { Modal, Button, Row, Col, Badge, Table, Image } from "react-bootstrap";
import {
  FaMapMarkerAlt,
  FaCar,
  FaPhone,
  FaEnvelope,
  FaClock,
} from "react-icons/fa";

const YardDetailsModal = ({ show, onHide, yard }) => {
  if (!yard) return null;

  const getSpotStatusBadge = (status) => {
    switch (status) {
      case "available":
        return <Badge bg="success">Available</Badge>;
      case "occupied":
        return <Badge bg="danger">Occupied</Badge>;
      case "reserved":
        return <Badge bg="warning">Reserved</Badge>;
      case "maintenance":
        return <Badge bg="secondary">Maintenance</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const getSpotSizeBadge = (size) => {
    switch (size) {
      case "small":
        return <Badge bg="info">Small</Badge>;
      case "medium":
        return <Badge bg="primary">Medium</Badge>;
      case "large":
        return <Badge bg="warning">Large</Badge>;
      default:
        return <Badge bg="secondary">{size}</Badge>;
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Yard Details: {yard.yardName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-4">
          <Col md={12}>
            {yard.yardImage && (
              <Image
                src={yard.yardImage}
                alt={yard.yardName}
                fluid
                rounded
                className="mb-3"
              />
            )}
          </Col>
        </Row>

        <Row className="mb-4">
          <Col md={6}>
            <h6 className="text-muted">Basic Information</h6>
            <Table borderless size="sm">
              <tbody>
                <tr>
                  <td>
                    <strong>Yard Name:</strong>
                  </td>
                  <td>{yard.yardName}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Status:</strong>
                  </td>
                  <td>
                    <Badge
                      bg={
                        yard.yardStatus === "operational"
                          ? "success"
                          : "warning"
                      }
                    >
                      {yard.yardStatus}
                    </Badge>
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Active:</strong>
                  </td>
                  <td>
                    <Badge bg={yard.isActive ? "success" : "secondary"}>
                      {yard.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Verified:</strong>
                  </td>
                  <td>
                    <Badge bg={yard.isVerified ? "success" : "warning"}>
                      {yard.isVerified ? "Verified" : "Unverified"}
                    </Badge>
                  </td>
                </tr>
              </tbody>
            </Table>
          </Col>

          <Col md={6}>
            <h6 className="text-muted">Location Details</h6>
            <Table borderless size="sm">
              <tbody>
                <tr>
                  <td>
                    <FaMapMarkerAlt className="me-2" />
                    <strong>Address:</strong>
                  </td>
                  <td>{yard.yardAddress}</td>
                </tr>
                <tr>
                  <td>
                    <strong>City:</strong>
                  </td>
                  <td>{yard.city}</td>
                </tr>
                <tr>
                  <td>
                    <strong>State:</strong>
                  </td>
                  <td>{yard.state}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Coordinates:</strong>
                  </td>
                  <td>
                    Lat: {yard.coordinates?.lat?.toFixed(6)}, Lng:{" "}
                    {yard.coordinates?.lng?.toFixed(6)}
                  </td>
                </tr>
              </tbody>
            </Table>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col md={6}>
            <h6 className="text-muted">Capacity Information</h6>
            <Table borderless size="sm">
              <tbody>
                <tr>
                  <td>
                    <FaCar className="me-2" />
                    <strong>Parked Vehicles:</strong>
                  </td>
                  <td>{yard.parkedVehicles || 0}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Occupancy Limit:</strong>
                  </td>
                  <td>{yard.occupancy || "Not set"}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Utilization:</strong>
                  </td>
                  <td>
                    {yard.occupancy
                      ? `${Math.round(((yard.parkedVehicles || 0) / yard.occupancy) * 100)}%`
                      : "N/A"}
                  </td>
                </tr>
              </tbody>
            </Table>
          </Col>

          <Col md={6}>
            <h6 className="text-muted">Timestamps</h6>
            <Table borderless size="sm">
              <tbody>
                {yard.createdAt && (
                  <tr>
                    <td>
                      <FaClock className="me-2" />
                      <strong>Created:</strong>
                    </td>
                    <td>{new Date(yard.createdAt).toLocaleDateString()}</td>
                  </tr>
                )}
                {yard.updatedAt && (
                  <tr>
                    <td>
                      <strong>Last Updated:</strong>
                    </td>
                    <td>{new Date(yard.updatedAt).toLocaleDateString()}</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>

        {yard.parkingSpots && yard.parkingSpots.length > 0 && (
          <>
            <h6 className="text-muted mb-3">
              Parking Spots ({yard.parkingSpots.length})
            </h6>
            <div className="table-responsive">
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>Spot #</th>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Size</th>
                    <th>Vehicle</th>
                  </tr>
                </thead>
                <tbody>
                  {yard.parkingSpots.slice(0, 10).map((spot, index) => (
                    <tr key={index}>
                      <td>{spot.spotNumber}</td>
                      <td>{spot.spotName || "-"}</td>
                      <td>{getSpotStatusBadge(spot.status)}</td>
                      <td>{getSpotSizeBadge(spot.size)}</td>
                      <td>{spot.vehicle ? "Assigned" : "Available"}</td>
                    </tr>
                  ))}
                  {yard.parkingSpots.length > 10 && (
                    <tr>
                      <td colSpan={5} className="text-center text-muted">
                        ... and {yard.parkingSpots.length - 10} more spots
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default YardDetailsModal;
